import {
  type MutableRefObject,
  // useCallback,
  useEffect,
  useMemo,
  // useRef,
  useState,
} from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Button, Typography } from 'antd';

import { Drawer } from '@/shared/components/drawer';
import { COLORS } from '@/shared/constants/colors';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { PickDtoFilter } from '@/shared/lib/forms/form-wrapper.types';
import { commonfilterCollection } from '@/shared/lib/hooks/use-form-render/helpers';
import {
  CollectionName,
  CollectionsTypeValueMap,
  ICollections,
} from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { ArrayOrOne } from '@/shared/types/utility-types';

// import { isEmpty } from '@/shared/utils/misc';
import { Icon } from '../../icon';
import { Input } from '../../input';
import { Container, Row } from '../../layout';

import { ListItem } from './ListItem';

// TODO rework
type CommonDto = {
  id: string;
  name?: string;
  nickname?: string;
  nickName?: string;
  shortname?: string;
  createdAt?: Date;
  isTest?: boolean;
  selectedCountry?: string;
  // TODO rework [WA-325]
  startDate?: string;
};

export const DrawerPickArrayOfDto = <
  K extends CollectionName,
  T extends CollectionsTypeValueMap[K],
>({
  label,
  control,
  collection,
  name,
  mode,
  memoValue,
  // deps,
  type,
  isOpen,
  setIsOpen,
  setValue,
  filter = (item) => !!item,
  onFieldChange,
}: {
  label: string;
  required?: boolean;
  control: Control;
  collection: ArrayOrOne<keyof ICollections>;
  name: string;
  mode?: 'multiple' | 'tags';
  tooltip?: string;
  memoValue: MutableRefObject<Record<string, unknown>>;
  deps?: Array<string>;
  type?: string | string[];
  isOpen: boolean;
  setIsOpen: (arg: boolean) => void;
  setValue: UseFormSetValue<Record<typeof name, unknown>>;
  filter?: PickDtoFilter;
  onFieldChange?: (value: unknown) => void;
}) => {
  const { t } = useTranslation('common');
  const router = useAppRouter();
  const sjDb = useSJDatabase();

  const [inputSearch, setInputSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [lastList, setLastList] = useState<object[] | null>(null);

  const close = () => {
    setSelectedItems([]);
    setIsOpen(false);
  };

  const onSelectChange = (value: unknown) => {
    memoValue.current[name] = value;
    onFieldChange?.(value);
  };

  const handleSelectClick = () => {
    if (mode === 'multiple') {
      onSelectChange(selectedItems);
    } else {
      onSelectChange(selectedItems[0]);
    }
    close();
  };

  const handleClose = () => {
    close();
  };

  const inputHandler = (e: { target: { value: string } }) => {
    const lowerCase = e.target.value.toLowerCase().trim();
    setInputSearch(lowerCase);
  };

  const handleClickItem = (value: string) => {
    if (mode === 'multiple') {
      let result: string[];
      if (selectedItems.includes(value)) {
        result = selectedItems.filter((i) => i !== value);
      } else {
        result = selectedItems.slice(0);
        result.push(value);
      }
      setSelectedItems(result);
    } else {
      setSelectedItems([value]);
    }
  };

  const onAddNewClick = async () => {
    if (!type && collection !== CollectionName.BENEFICIARIES) return;
    const collectionToCreate = Array.isArray(collection) ? collection[0] : collection;

    await router.replace({
      pathname: window.location.pathname,
      query: {
        fastAddCollection: collectionToCreate,
        fastAddTypes: type,
        fastTrack: true,
      },
    });
  };

  const filteredData = useMemo(() => {
    if (!isOpen) {
      return null;
    }
    const filterCollection = (item: CommonDto) => {
      return commonfilterCollection(item) && filter(item as T, memoValue.current, name);
    };

    const prepareData = (item: CommonDto) => {
      return {
        value: item.id,
        label: item.name || item.nickName || item.nickname || item.shortname,
        createdAt: item.createdAt,
        country: item.selectedCountry,
        // TODO rework [WA-325]: actualy startDate is string, not Date
        startDate: item?.startDate ? new Date(item.startDate) : undefined,
      };
    };

    const collectionsList = Array.isArray(collection)
      ? (collection as Array<keyof ICollections>).map((col) => sjDb.collections[col])
      : [sjDb.collections[collection as keyof ICollections]];

    const dtos = collectionsList
      .map((col) => col?.findMany(filterCollection).map(prepareData) || [])
      .flat();

    return inputSearch === ''
      ? dtos.slice(0)
      : dtos.filter((el) => {
          if (el.label) {
            return el.label.toLowerCase().includes(inputSearch);
          } else {
            return el;
          }
        });
  }, [collection, sjDb, isOpen, inputSearch]);

  useEffect(() => {
    if (filteredData === null) {
      return;
    }

    if (
      lastList === null &&
      (Array.isArray(collection) || sjDb.collections[collection] !== undefined)
    ) {
      setLastList(filteredData);
    }

    // // WTF
    // if (lastList !== null && lastList.length !== filteredData.length) {
    //   const item = filteredData[filteredData.length - 1];
    //   if (item) {
    //     handleClickItem(item.value);
    //   }
    //   setLastList(filteredData);
    // }
  }, [filteredData]);

  useEffect(() => {
    if (memoValue.current[name] && !control._formValues[name]) {
      setValue(name, memoValue.current[name]);
    }
  }, [memoValue.current, name]);

  useEffect(() => {
    if (isOpen && memoValue.current[name] instanceof Array) {
      setSelectedItems(memoValue.current[name] as string[]);
    }
  }, [isOpen]);

  return (
    <Drawer
      height={'80vh'}
      placement="bottom"
      open={isOpen}
      onClose={handleClose}
      closeIcon={false}
      footer={
        <Row direction="column" gap={12} paddingX={14}>
          <Button
            type="primary"
            size="large"
            onClick={handleSelectClick}
            disabled={selectedItems.length === 0}
          >
            {t('select')}
          </Button>

          <Button size="large" onClick={onAddNewClick}>
            {t('Add')}
          </Button>
        </Row>
      }
    >
      <Row justifyContent="space-between" alignCenter nowrap>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {label}
        </Typography.Title>

        <Icon icon="close" cursorPointer onClick={handleClose} color={COLORS.colorLink} />
      </Row>

      <Container marginTop={24}>
        <Input placeholder="Search" onChange={inputHandler} />
      </Container>

      <Container marginTop={24} paddingLeft={8}>
        {filteredData?.length === 0 ? (
          <p>There is no data added yet, fill the info in the contact section</p>
        ) : (
          filteredData?.map((item, index) => {
            const isSelected = item?.value !== undefined && selectedItems.includes(item?.value);
            // {!!value && selectedItem.includes(value) && (
            //value={item?.value}
            return (
              <div key={index} onClick={() => handleClickItem(item.value)}>
                <ListItem
                  isSelected={isSelected}
                  title={item?.label}
                  showStartDate={collection === CollectionName.PRIVATE_DOCUMENTS}
                  date={item.createdAt}
                  startDate={item.startDate}
                  country={item?.country}
                />
              </div>
            );
          })
        )}
      </Container>
    </Drawer>
  );
};
